import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { GovAccordion, GovIcon, GovMessage } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DataBoxFolderType,
	useDataBoxesContext,
} from '@gov-nx/module/data-box';
import type { Storage } from '@gov-nx/module/page';
import { PageCode } from '@gov-nx/module/page';
import { useDataBoxStore } from '@gov-nx/store';
import { AccordionItem, LayoutGap } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { StorageUsage } from './StorageUsage';

export enum DataBoxStorageUsageVariant {
	Accordeon = 'Accordeon',
}

interface DataBoxStorageUsageProps {
	variant?: DataBoxStorageUsageVariant;
}

export const DataBoxStorageUsage = ({ variant }: DataBoxStorageUsageProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const { t } = useTranslation([PageCode['datove-schranky']]);
	const { storage, dataBoxes } = useDataBoxesContext();
	const isStorageAccordionOpen = useDataBoxStore('isStorageAccordionOpen');
	const setIsStorageAccordionOpen = useDataBoxStore(
		'setIsStorageAccordionOpen'
	);

	const getStorageContent = (storage: Storage) => (
		<>
			<LayoutGap gap="s">
				<div className="rounded overflow-hidden bg-(--background-block-primary) mb-2">
					<div
						className="rounded-l bg-(--background-status-error) h-4"
						style={{
							width: `${
								storage.isStorageFull ? 100 : storage.usedInPercentInt
							}%`,
						}}
					/>
				</div>
				<dl className="flex justify-between mb-4 text-body-xs text-(--text-secondary)">
					<StorageUsage storage={storage} />
				</dl>
			</LayoutGap>
			<GovMessage
				color="error"
				type="subtle"
				icon={
					<GovIcon
						name="lightbulb-fill"
						type="components"
						size="l"
						color="error"
					/>
				}>
				<p>
					<Trans
						t={t}
						i18nKey="uloziste.procisteni"
						components={{
							'odkaz-archiv': (
								<Link
									to={
										dataBoxes[0]?.datovaSchrankaId
											? pageRouteResolver('datove-schranky.schranka', {
													dataBoxId: dataBoxes[0]?.datovaSchrankaId,
													folderType: DataBoxFolderType.Archived,
												})
											: pageRouteResolver('datove-schranky')
									}
								/>
							),
							'odkaz-dokumenty': (
								<Link to={pageRouteResolver('moje-soubory')} />
							),
						}}
					/>
				</p>
			</GovMessage>
		</>
	);

	if (!storage.storageUsage) return null;
	if (storage.storageUsage.hasEnoughFreeSpace) return null;

	const storageContent = getStorageContent(storage.storageUsage);

	if (variant === DataBoxStorageUsageVariant.Accordeon) {
		return (
			<GovAccordion
				size="xs"
				className="[ ds:hidden! ]"
				noBorder={true}
				aria-labelledby="uloziste-portalu-obcana">
				<AccordionItem
					isOpen={isStorageAccordionOpen}
					onChange={(e) => setIsStorageAccordionOpen(e)}
					label={
						<h3 id="uloziste-portalu-obcana">{tn('uloziste.uloziste')}</h3>
					}>
					<LayoutGap gap="m">{storageContent}</LayoutGap>
				</AccordionItem>
			</GovAccordion>
		);
	}

	return (
		<LayoutGap
			gap="m"
			className="hidden! [ xl:flex! xl:mb-(--templates-margin-m)! ]">
			<h3 className="text-body-m-bold! text-(--text-secondary)">
				{tn('uloziste.uloziste')}
			</h3>
			{storageContent}
		</LayoutGap>
	);
};
